/**
 * OwnerMoreMenu.js - COMPLETE More menu for Owner Dashboard
 * Contains ALL features - nothing left out
 * Styled like Worker's More menu with colored icons and card sections
 */
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Home, Calendar, Users, User, ChevronRight, LogOut,
  Briefcase, Clock, DollarSign, Settings, FileText,
  Star, Heart, Upload, Award, AlertTriangle, CreditCard,
  ClipboardList, Phone, BookOpen, Shield, UserCheck,
  BarChart3, Headphones, Package, Truck, Sparkles, Shirt,
  CheckCircle, Bell, Receipt, GraduationCap, ShoppingBag,
  Plus, Tag, CalendarDays, UserPlus, ClipboardCheck, ScrollText,
  PhoneOff, Mail, FileEdit, Image, MessageCircle, MessageSquare, Flag, History,
  Archive, Banknote, Car, UserX, Send, TestTube, StickyNote, FolderOpen,
  Clipboard, Building, MapPin, Eye, Box, ListChecks, Wallet, ShoppingCart
} from 'lucide-react';

const OwnerMoreMenu = ({ activeTab, setActiveTab, onLogout }) => {
  
  const MORE_MENU_SECTIONS = [
    // BOOKINGS & SERVICES (No Cleaning)
    { section: 'Bookings & Services', items: [
      { id: 'bookings', label: 'All Bookings', icon: Calendar, color: 'blue' },
      { id: 'rides', label: 'Rides & Transport', icon: Car, color: 'purple' },
      { id: 'courier', label: 'Courier/Packages', icon: Truck, color: 'orange' },
      { id: 'errands-bookings', label: 'Errands Bookings', icon: ShoppingBag, color: 'pink' },
      { id: 'laundry-bookings', label: 'Laundry Bookings', icon: Shirt, color: 'pink' },
      { id: 'packages', label: 'Package Tracking', icon: Box, color: 'blue' },
    ]},
    // QUICK ACTIONS
    { section: 'Quick Actions', items: [
      { id: 'phone', label: 'Phone Booking', icon: Phone, color: 'gold' },
      { id: 'addworker', label: 'Add Worker', icon: UserPlus, color: 'green' },
      { id: 'quickbook', label: 'Book a Service', icon: Plus, color: 'blue' },
      { id: 'postjob', label: 'Post Job', icon: Briefcase, color: 'purple' },
    ]},
    // PEOPLE & WORKERS
    { section: 'People & Workers', items: [
      { id: 'workers', label: 'All Workers', icon: Users, color: 'blue' },
      { id: 'customers', label: 'Customers', icon: UserCheck, color: 'green' },
      { id: 'leads', label: 'CRM Leads', icon: UserPlus, color: 'purple' },
      { id: 'applications', label: 'Applications', icon: UserPlus, color: 'amber' },
      { id: 'onboarding', label: 'Onboarding', icon: ClipboardCheck, color: 'purple' },
    ]},
    // JOB MANAGEMENT
    { section: 'Job Management', items: [
      { id: 'jobboard', label: 'Job Board', icon: Briefcase, color: 'blue' },
      { id: 'pickupjobs', label: 'Pickup Jobs', icon: ListChecks, color: 'green' },
      { id: 'myjobs', label: 'My Jobs', icon: Clipboard, color: 'purple' },
      { id: 'jobapprovals', label: 'Job Approvals', icon: CheckCircle, color: 'green' },
      { id: 'declinedjobs', label: 'Declined Jobs', icon: UserX, color: 'red' },
    ]},
    // FINANCE & PAYMENTS
    { section: 'Finance & Payments', items: [
      { id: 'payroll', label: 'Payroll', icon: DollarSign, color: 'green' },
      { id: 'funds', label: 'Payments & Funds', icon: CreditCard, color: 'blue' },
      { id: 'payments', label: 'Payment History', icon: Wallet, color: 'purple' },
      { id: 'timerecords', label: 'Time Records', icon: Clock, color: 'amber' },
      { id: 'clockinout', label: 'Clock In/Out', icon: Clock, color: 'green' },
      { id: 'tips', label: 'Tips', icon: Heart, color: 'pink' },
      { id: 'receipts', label: 'Receipts', icon: Receipt, color: 'amber' },
      { id: 'cashverify', label: 'Cash Verify', icon: Banknote, color: 'green' },
      { id: 'driver-payouts', label: 'Driver Payouts', icon: Car, color: 'blue' },
    ]},
    // SERVICES & SETTINGS
    { section: 'Services & Settings', items: [
      { id: 'services', label: 'Manage Services', icon: Package, color: 'purple' },
      { id: 'pricing', label: 'Pricing Settings', icon: DollarSign, color: 'green' },
      { id: 'addons', label: 'Add-ons', icon: Plus, color: 'blue' },
      { id: 'coupons', label: 'Coupons', icon: Tag, color: 'amber' },
      { id: 'schedule', label: 'Master Schedule', icon: CalendarDays, color: 'purple' },
      { id: 'careers', label: 'Careers/Job Posts', icon: Briefcase, color: 'blue' },
    ]},
    // TEAM MANAGEMENT
    { section: 'Team Management', items: [
      { id: 'evaluations', label: 'Evaluations', icon: Star, color: 'yellow' },
      { id: 'policies', label: 'Policies', icon: ScrollText, color: 'amber' },
      { id: 'education', label: 'Training', icon: GraduationCap, color: 'blue' },
      { id: 'callouts', label: 'Call Outs', icon: PhoneOff, color: 'orange' },
      { id: 'idcards', label: 'ID Cards', icon: CreditCard, color: 'purple' },
      { id: 'certifications', label: 'Certifications', icon: Award, color: 'amber' },
      { id: 'notes', label: 'Worker Notes', icon: StickyNote, color: 'yellow' },
    ]},
    // COMMUNICATION
    { section: 'Communication', items: [
      { id: 'messages', label: 'Messages', icon: MessageSquare, color: 'blue' },
      { id: 'support', label: 'Support Center', icon: Headphones, color: 'purple' },
      { id: 'commcenter', label: 'Communication Center', icon: Phone, color: 'green' },
      { id: 'contactsubmissions', label: 'Contact Forms', icon: ClipboardList, color: 'amber' },
      { id: 'customrequests', label: 'Custom Requests', icon: FileText, color: 'orange' },
    ]},
    // EMAIL & SMS
    { section: 'Email & SMS', items: [
      { id: 'email', label: 'Email Center', icon: Mail, color: 'blue' },
      { id: 'emailtemplates', label: 'Email Templates', icon: FileEdit, color: 'purple' },
      { id: 'emailsettings', label: 'Email Settings', icon: Settings, color: 'gray' },
      { id: 'failedemails', label: 'Failed Emails', icon: AlertTriangle, color: 'red' },
      { id: 'smslogs', label: 'SMS Logs', icon: MessageCircle, color: 'green' },
    ]},
    // FILES & FORMS
    { section: 'Files & Forms', items: [
      { id: 'uploads', label: 'File Uploads', icon: Upload, color: 'blue' },
      { id: 'gallery', label: 'Photo Gallery', icon: Image, color: 'purple' },
      { id: 'photogallery', label: 'All Photos', icon: Image, color: 'pink' },
      { id: 'customerforms', label: 'Customer Forms', icon: FileText, color: 'amber' },
      { id: 'completedforms', label: 'Completed Forms', icon: ClipboardCheck, color: 'green' },
      { id: 'formtemplates', label: 'Form Templates', icon: FileEdit, color: 'purple' },
      { id: 'serviceforms', label: 'Service Forms', icon: Clipboard, color: 'blue' },
      { id: 'customerfiles', label: 'Customer Files', icon: FolderOpen, color: 'amber' },
    ]},
    // REPORTS & ANALYTICS
    { section: 'Reports & Analytics', items: [
      { id: 'reports', label: 'Reports', icon: BarChart3, color: 'blue' },
      { id: 'visitor-analytics', label: 'Visitor Analytics', icon: Eye, color: 'purple' },
      { id: 'reviews', label: 'Customer Reviews', icon: Star, color: 'yellow' },
      { id: 'feedback', label: 'Customer Feedback', icon: MessageCircle, color: 'purple' },
      { id: 'incidents', label: 'Incidents', icon: AlertTriangle, color: 'red' },
      { id: 'disputes', label: 'Disputes', icon: Flag, color: 'orange' },
      { id: 'activitylogs', label: 'Activity Logs', icon: History, color: 'gray' },
      { id: 'auditlogs', label: 'Audit Logs', icon: Eye, color: 'purple' },
    ]},
    // ARCHIVE & SETTINGS
    { section: 'Archive & Settings', items: [
      { id: 'archive', label: 'Archive', icon: Archive, color: 'gray' },
      { id: 'autoarchive', label: 'Auto Archive', icon: Archive, color: 'purple' },
      { id: 'reminders', label: 'Reminders', icon: Bell, color: 'amber' },
      { id: 'testcenter', label: 'Test Center', icon: TestTube, color: 'blue' },
      { id: 'myprofile', label: 'My Profile', icon: User, color: 'blue' },
    ]},
  ];

  const getColorClass = (color) => {
    const colors = {
      green: 'bg-green-500/10 text-green-500',
      blue: 'bg-blue-500/10 text-blue-500',
      purple: 'bg-purple-500/10 text-purple-500',
      amber: 'bg-amber-500/10 text-amber-500',
      orange: 'bg-orange-500/10 text-orange-500',
      red: 'bg-red-500/10 text-red-500',
      yellow: 'bg-yellow-500/10 text-yellow-500',
      pink: 'bg-pink-500/10 text-pink-500',
      gray: 'bg-gray-500/10 text-gray-500',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="min-h-screen bg-[var(--background)] pb-24" data-testid="owner-more-menu">
      {/* Header */}
      <div className="bg-[var(--background-paper)] border-b border-[var(--border-primary)] px-4 py-4">
        <h1 className="text-xl font-bold text-[var(--text-primary)]">More</h1>
        <p className="text-sm text-[var(--text-muted)]">Access all features</p>
      </div>
      
      <div className="p-4 space-y-4">
        {MORE_MENU_SECTIONS.map((section, sectionIdx) => (
          <motion.div 
            key={section.section} 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: sectionIdx * 0.03 }}
            className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl overflow-hidden"
          >
            {/* Section Header */}
            <div className="px-4 py-2 bg-[var(--background-subtle)] border-b border-[var(--border-primary)]">
              <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
                {section.section}
              </h3>
            </div>
            
            {/* Section Items */}
            {section.items.map((item, idx) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              
              return (
                <React.Fragment key={item.id}>
                  {idx > 0 && <div className="border-t border-[var(--border-primary)]" />}
                  <button
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                      isActive 
                        ? 'bg-[var(--primary)]/10' 
                        : 'hover:bg-[var(--background-subtle)]'
                    }`}
                    data-testid={`owner-more-${item.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getColorClass(item.color)}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className={`text-[var(--text-primary)] ${isActive ? 'font-medium' : ''}`}>
                        {item.label}
                      </span>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${isActive ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'}`} />
                  </button>
                </React.Fragment>
              );
            })}
          </motion.div>
        ))}
        
        {/* Logout Button */}
        <button 
          onClick={onLogout}
          className="w-full px-4 py-3 flex items-center justify-center gap-2 bg-red-500/10 text-red-500 rounded-xl font-medium hover:bg-red-500/20 transition-colors"
          data-testid="owner-more-logout"
        >
          <LogOut className="w-5 h-5" />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  );
};

export default OwnerMoreMenu;
